import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, CircleAlert, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { strengthPct } from "@/pages/candidate/profile/parts";
import { cn } from "@/lib/utils";
import { companyLine, fitTone } from "./portal";

function Confirm({ o, profile, onClose, onSent }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const j = o.job;
  const send = async () => {
    setBusy(true);
    try { await api.post(`/jobs/${j.id}/apply`, { note }); onSent(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not send your application"); }
    finally { setBusy(false); }
  };
  const items = [`Your profile (${strengthPct(profile)}% complete)`, `Contact: email${profile?.contact?.phone ? " and phone" : ""}`];
  return (
    <>
      <DialogHeader className="px-6 pt-6 pb-4 border-b border-border text-left">
        <DialogTitle className="text-xl font-display" data-testid="apply-dialog-title">Apply to {j.title}</DialogTitle>
        <DialogDescription>{companyLine(o.company_name, j)}</DialogDescription>
      </DialogHeader>
      <div className="px-6 py-5 space-y-4">
        <div>
          <div className="text-sm font-semibold text-muted-foreground mb-2.5">{o.company_name || "The employer"} will receive</div>
          <ul className="space-y-2 text-sm">{items.map((t) => <li key={t} className="flex items-center gap-2.5"><Check className="h-4 w-4 text-primary" />{t}</li>)}</ul>
        </div>
        {o.gaps?.[0] && <div className="rounded-lg bg-amber-500/10 border border-amber-500/20 px-3.5 py-2.5 text-sm text-amber-800 dark:text-amber-200 flex gap-2" data-testid="apply-gap-note"><CircleAlert className="h-4 w-4 shrink-0 mt-0.5" /><span>Heads-up from your fit check: {o.gaps[0]}</span></div>}
        <div>
          <label htmlFor="apply-note" className="text-sm font-medium">Short note <span className="text-muted-foreground font-normal">· optional</span></label>
          <Textarea id="apply-note" maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why this role?" className="mt-2 min-h-[96px]" data-testid="apply-note" />
        </div>
      </div>
      <DialogFooter className="px-6 py-4 border-t border-border gap-2 sm:gap-2">
        <Button variant="outline" onClick={onClose} data-testid="apply-cancel">Cancel</Button>
        <Button onClick={send} disabled={busy} data-testid="apply-send">{busy && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}Send application</Button>
      </DialogFooter>
    </>
  );
}

function Sent({ o, similar, onClose, onPick }) {
  const nav = useNavigate();
  return (
    <div className="px-6 pt-8 pb-6 text-center" data-testid="apply-sent">
      <span className="mx-auto h-14 w-14 rounded-full bg-primary/15 text-primary flex items-center justify-center hs-rise"><Check className="h-6 w-6" /></span>
      <DialogTitle className="mt-4 text-2xl font-display">Application sent</DialogTitle>
      <DialogDescription className="mt-2 text-[15px] leading-relaxed">The next step is a short assessment for this role. Start it now, or any time from Applications.</DialogDescription>
      {similar.length > 0 && (
        <div className="mt-5 rounded-xl border border-border p-4 text-left">
          <div className="text-sm text-muted-foreground mb-1.5">Similar roles you might like</div>
          {similar.map((s) => (
            <button key={s.job.id} onClick={() => onPick(s.job.id)} className="w-full flex justify-between gap-3 py-1.5 text-sm hover:text-primary transition-colors" data-testid={`apply-similar-${s.job.id}`}>
              <span className="truncate">{s.job.title}{s.company_name ? ` · ${s.company_name}` : ""}</span>
              <span className={cn("tabular-nums", fitTone(s.fit))}>{Math.round(s.fit)}%</span>
            </button>
          ))}
        </div>
      )}
      <div className="mt-5 grid grid-cols-2 gap-3">
        <Button variant="outline" onClick={onClose} data-testid="apply-keep-browsing">Keep browsing</Button>
        <Button onClick={() => nav(`/candidate/apply/${o.job.id}`)} data-testid="apply-start-assessment">Start assessment</Button>
      </div>
    </div>
  );
}

export function ApplyDialog({ o, profile, similar, onClose, onApplied, onPick }) {
  const [sent, setSent] = useState(false);
  const close = () => { setSent(false); onClose(); };
  return (
    <Dialog open={!!o} onOpenChange={(v) => !v && close()}>
      <DialogContent className="sm:max-w-[520px] p-0 gap-0 overflow-hidden" data-testid="apply-dialog">
        {o && (sent
          ? <Sent o={o} similar={similar} onClose={close} onPick={(id) => { close(); onPick(id); }} />
          : <Confirm o={o} profile={profile} onClose={close} onSent={() => { setSent(true); onApplied(); }} />)}
      </DialogContent>
    </Dialog>
  );
}
